import $ from 'jquery';
import * as Sentry from '@sentry/browser';
import DOMPurify from 'dompurify';

// Moved randomId to its own file to avoid importing jQuery and Sentry
export { randomId } from './random_id';

//
// Convert a class into a JQuery plugin. The class constructor must be of the
// form: function(element, options)
//
export const pluginify = function (pluginName, PluginClass) {
  $.fn[pluginName] = function (option, ...args) {
    return this.each(function () {
      let data;
      const $this = $(this);

      if (!(data = $this.data(pluginName))) {
        $this.data(pluginName, (data = new PluginClass($this, option)));
      }
      if (typeof option === 'string') {
        return data[option](...args);
      }
    });
  };
};

export const getIsSharedWebpage = function () {
  // accessing window.parent properties might result in an
  // error if it has a different domain.
  // https://developer.mozilla.org/en-US/docs/Web/Security/Same-origin_policy#Cross-origin_script_API_access
  // so, we need to catch a possible SecurityError

  let isSharedWebpage = false;
  try {
    isSharedWebpage = window.isSharedWebpage || window.parent.isSharedWebpage;
  } catch (e) {
    // We are most likely in an iframe with a cross-origin parent.
    // This also means we are likely not authenticated (since we don't support third-party cookies).
    // We probably want the more-restrictive behavior of a shared webpage.
    isSharedWebpage = true;
  }
  return isSharedWebpage;
};

export const isLocalStorageSupported = function () {
  const testKey = 'testKey';
  try {
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

export const setLocalStoragePreference = function (key, value) {
  if (isLocalStorageSupported()) {
    localStorage.setItem(key, value);
  }
};

export const getLocalStoragePreference = function (key) {
  if (isLocalStorageSupported()) {
    return localStorage.getItem(key);
  }
};

export const removeLocalStoragePreference = function (key) {
  if (isLocalStorageSupported()) {
    localStorage.removeItem(key);
  }
};

export const setLocalStorageJSONPreference = function (key, value) {
  setLocalStoragePreference(key, JSON.stringify(value));
};

export const getLocalStorageJSONPreference = function (key) {
  try {
    const data = getLocalStoragePreference(key);
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
  } catch (ex) {
    Sentry.captureException(ex, { key });
  }
};

export const isSessionStorageSupported = function () {
  const testKey = 'testKey';
  try {
    sessionStorage.setItem(testKey, '1');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

export const setSessionStoragePreference = function (key, value) {
  if (isSessionStorageSupported()) {
    sessionStorage.setItem(key, value);
  }
};

export const getSessionStoragePreference = function (key) {
  if (isSessionStorageSupported()) {
    return sessionStorage.getItem(key);
  }
};

export const setSessionStorageJSONPreference = function (key, value) {
  setSessionStoragePreference(key, JSON.stringify(value));
};

export const getSessionStorageJSONPreference = function (key) {
  try {
    return JSON.parse(getSessionStoragePreference(key));
  } catch (ex) {
    Sentry.captureException(ex, { key });
  }
};

// Shallow array comparator that compares each value within each array
export const arrayComparator = (arrayA, arrayB) => {
  // check for undefined arrays first
  if (!arrayA && arrayB) return -1;
  if (arrayA && !arrayB) return 1;
  if (!arrayA && !arrayB) return 0;

  let comparison = 0;

  // stop at the first different value
  arrayA.find((valueA, index) => {
    const valueB = arrayB[index];

    // if arrayA has more values than arrayB,
    // it's considered less than arrayB
    if (!valueB || valueA > valueB) {
      comparison = 1;
      return true;
    }

    if (valueA < valueB) {
      comparison = -1;
      return true;
    }
  });

  // we didn't find a value in arrayA that is different than a value in
  // arrayB but B has more values
  // ex: [1, 2] vs [1, 2, 3]
  if (comparison === 0 && arrayA.length < arrayB.length) return -1;

  // return the comparison value found in the loop or the initial 0
  // if all the values were equal and the arrays are of the same length
  return comparison;
};

export const htmlEscape = text => {
  const textArea = document.createElement('textarea');
  textArea.textContent = text;
  return textArea.innerHTML;
};

const allowedTags = [
  'aha-promo-card',
  'aha-tooltip',
  'aha-score-pill',
  'aha-avatar',
  'aha-record-reference',
];
const allowedCustomAttributes = ['with-overlay', 'score', 'clickable'];
const allowedAddAttributes = ['target']; // This could be dangerous, allow only when link is added safely
export const sanitizeHtml = (html, addAttributes = []) => {
  // sanitize accepts an array of strings to match .html()
  // https://github.com/jquery/jquery/blob/3.x-stable/src/manipulation.js#L402
  // which calls into access() which iterates the array via recursion here
  // https://github.com/jquery/jquery/blob/3.x-stable/src/core/access.js#L20
  if (Array.isArray(html)) {
    return sanitizeHtml(html.join(''));
  }
  // special handling for non-string values that need to be pre-processed
  //  yet still checked for safety upon usage.
  if (html && (html.sanitizerShouldSkip || html.hasBeenSanitized)) return html;
  if (html && typeof html !== 'string')
    throw new Error('HTML must either be a string or marked as pre-sanitized.');

  return DOMPurify.sanitize(html, {
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: tag => allowedTags.includes(tag), // allow all tags from the allowed list
      attributeNameCheck: attr => allowedCustomAttributes.includes(attr), // allow all attributes from the allowed list
    },
    USE_PROFILES: { html: true, svg: true },
    ADD_ATTR: addAttributes.filter(attr => allowedAddAttributes.includes(attr)),
  });
};

export const containsOnlyDigits = text => {
  return /^\d+$/.test(text);
};

export const safe$ = selector => {
  let element;
  if (typeof selector === 'string') {
    element = $(sanitizeHtml(selector));
  } else if (selector instanceof $ || selector instanceof HTMLElement) {
    element = selector; // already sanitized - and if not, XSS execution will have already occured.
  } else {
    throw new Error(
      'Invalid selector type. Must be a string, jQuery object, or HTMLElement.'
    );
  }
  // @ts-ignore
  element.sanitizerShouldSkip = true;
  return element;
};

export const isSmallScreen = () => window.innerWidth <= 960;

export const randomRange = (min, max) =>
  min + Math.floor(Math.random() * (max - min));

export const isRelativeUrl = url =>
  new URL(document.baseURI).origin === new URL(url, document.baseURI).origin;

export const appendQueryParams = (url, params) => {
  const urlObj = new URL(url, window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    urlObj.searchParams.set(key, value);
  });

  return urlObj.toString();
};

/**
 * Waits for an element to become visible.
 *
 * @param {HTMLElement} element - The DOM element to wait for visibility
 * @param {AbortSignal} [signal] - Optional AbortSignal for cancellation
 * @returns {Promise<HTMLElement>} Promise that resolves with the element when visible. Rejects if aborted.
 */
export const waitUntilVisible = (element, signal) => {
  return new Promise((resolve, reject) => {
    let resizeObserver = null;

    const cleanup = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    signal?.addEventListener('abort', () => {
      cleanup();
      reject(signal.reason);
    });

    if (element.offsetParent === null) {
      resizeObserver = new ResizeObserver(() => {
        if (element.offsetParent !== null) {
          cleanup();
          resolve(element);
        }
      });
      resizeObserver.observe(element);
    } else {
      resolve(element);
    }
  });
};
